import { useEffect, useRef, useState } from 'react';
import { BodySmallMidEmphasis } from '@/design-system';
import { IconButton, Tooltip } from '@mui/material';

interface Props {
  icon: React.ReactNode;
  name: string;
  onClick: () => void;
  tooltipText: string;
  tooltipDescId: string;
  containerClassName?: string;
  buttonClassName?: string;
}

export const TooltipButton = ({
  icon,
  name,
  onClick,
  tooltipText,
  tooltipDescId,
  containerClassName,
  buttonClassName,
}: Props) => {
  const [open, setOpen] = useState(false);
  const suppressOpen = useRef(false);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        suppressOpen.current = true;
        setOpen(false);
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open]);

  return (
    <>
      <span id={tooltipDescId} className="sr-only">
        {tooltipText}
      </span>
      <Tooltip
        open={open}
        onOpen={() => {
          if (!suppressOpen.current) setOpen(true);
        }}
        onClose={() => setOpen(false)}
        title={tooltipText}
        placement="top"
        enterTouchDelay={0}
      >
        <div
          className={containerClassName}
          onMouseLeave={() => {
            suppressOpen.current = false;
          }}
        >
          <IconButton
            className={buttonClassName}
            onClick={onClick}
            aria-label={name}
            aria-describedby={tooltipDescId}
          >
            {icon}
          </IconButton>
          <BodySmallMidEmphasis>{name}</BodySmallMidEmphasis>
        </div>
      </Tooltip>
    </>
  );
};
